import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, effect, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { BillingSubscription } from '@interfaces/subscription';
import { Role, User } from '@interfaces/user';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '@services/auth.service';
import { BillingService } from '@services/billing.service';
import { DeviceTypeService } from '@services/device-type.service';
import { NavigationService } from '@services/navigation.service';
import { UrlService } from '@services/url.service';
import { UserService } from '@services/user.service';
import { UtilsService } from '@services/utils.service';
import { getTranslation } from '@shared/translate-helper';
import { NgxSpinnerService } from 'ngx-spinner';
import { AvatarModule } from 'primeng/avatar';
import { Popover, PopoverModule } from 'primeng/popover';
import { of } from 'rxjs';
import { concatMap, delay, filter } from 'rxjs/operators';
import { MatModulesModule } from '../../shared/mat-modules/mat-modules.module';
import { PipesModule } from '../../shared/pipes/pipes.module';
import { AppIconComponent } from '../app-icon/app-icon.component';

@Component({
  selector: 'app-popover',
  templateUrl: './popover.component.html',
  styleUrls: ['./popover.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    AvatarModule,
    PopoverModule,
    AppIconComponent,
    TranslateModule,
    RouterModule,
    MatModulesModule,
    PipesModule,
  ],
  animations: [
    trigger('avatarAnim', [
      state('collapsed', style({ paddingLeft: '14px' })),
      state('expanded', style({ paddingLeft: '30px' })),
      transition('collapsed <=> expanded', animate('200ms ease-in-out')),
    ]),
  ],
})
export class PopoverComponent implements OnInit {
  public userEmail = '';
  public menuOpened = false;
  public isWorkspaceAdmin = false;
  public subscriptionName = '';
  public currentRouteUrl = '';
  public subscriptionPrice = 0;
  public trialTime = 0;
  public companySubscription?: BillingSubscription;
  public userData: User | null = null;
  private isMouseOverButton = false;
  private isMouseOverPopover = false;
  @Input() isSidebarExpanded = false;
  /**
   * `avatar` renders the circular trigger used in the global header (anchored
   * popover, opens on click).
   */
  @Input() variant: 'avatar' | 'row' = 'row';
  @Output() onLogout = new EventEmitter<void>();
  @ViewChild('op') popover!: Popover;
  @ViewChild('userMenuButton', { read: ElementRef }) userMenuButton!: ElementRef;

  constructor(
    private readonly userService: UserService,
    private readonly navigationService: NavigationService,
    private readonly authService: AuthService,
    private readonly billingService: BillingService,
    private readonly spinner: NgxSpinnerService,
    private readonly utilsService: UtilsService,
    private readonly translateService: TranslateService,
    public readonly urlService: UrlService,
    private readonly router: Router,
    private activatedRoute: ActivatedRoute,
    public readonly deviceType: DeviceTypeService,
  ) {
    effect(() => {
      if (this.userService.getUserData()) {
        this.loadUserData();
      }
    });
  }

  ngOnInit(): void {
    // Inicializar con la URL actual para que checkLinkActive funcioni des del primer render
    this.currentRouteUrl = this.router.url;

    this.activatedRoute.queryParams
      .pipe(
        concatMap((params) => {
          if (params && params['stripe'] === 'true') {
            return of(null).pipe(delay(1000));
          }
          // If 'stripe' is not 'true', proceed immediately
          return of(null);
        }),
      )
      .subscribe({
        next: () => {
          this.loadUserData();
          this.trackRouteChanges();
        },
        error: (err) => {
          this.handleError(err);
        },
      });
  }

  private handleError(err: any) {
    getTranslation(this.translateService, 'unexpected_error').subscribe((t) => {
      this.utilsService.showAlert({
        title: t,
        alertType: 'error',
        icon: 'circle-x',
        showActionBtn: false,
        timed: true,
        time: 5000,
      });
    });
    console.error('Error initializing popover:', err);
  }

  private loadUserData(): void {
    const user: User | null = this.userService.getUserData();

    if (!user) {
      console.error('User data not found');
      return;
    }
    this.userData = user;

    // this.userName = `${user.first_name} ${user.last_name}`;
    this.userEmail = user.email;
    this.isWorkspaceAdmin = [Role.ClientAdmin, Role.Admin].includes(user.role);
    this.trialTime = user.num_days_until_trial_expiration_date > -1 ? user.num_days_until_trial_expiration_date : 0;

    if (user.company?.uuid) {
      this.billingService.getCompanySubscription(user.company.uuid).subscribe({
        next: (resp: any) => {
          this.companySubscription = resp.subscription;

          this.subscriptionName = resp.subscription?.items?.data?.[0]?.price?.product?.name || '';

          if (resp.subscription?.status !== 'trialing') {
            this.subscriptionPrice = resp.subscription?.items?.data?.[0]?.price?.unit_amount / 100 || 0;
          }
        },
        error: (err) => {
          console.error('Error fetching subscription:', err);
        },
      });
    }
  }

  private trackRouteChanges(): void {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: any) => {
      this.currentRouteUrl = event.url;
    });
  }

  goToUpgradePlan(): void {
    this.navigationService.navigateToPricingPlans();
  }

  goToMyAccount(): void {
    this.navigationService.navigateToProfile();
  }

  goToSubscription(): void {
    this.navigationService.navigateToSubscription();
  }

  logout(): void {
    this.spinner.show();
    this.authService.logoutUser().subscribe({
      next: () => {
        this.authService.doLogout();
        this.onLogout.emit();
      },
      error: () => {
        getTranslation(this.translateService, 'error_logging_out').subscribe((t) => {
          this.utilsService.showAlert({
            title: t,
            alertType: 'error',
            icon: 'circle-x',
            showActionBtn: false,
            timed: true,
            time: 5000,
          });
        });
      },
    });
  }

  checkLinkActive(routes: string[]): boolean {
    return routes.some((route) => this.currentRouteUrl?.includes(route));
  }

  get hasActiveSubscription(): boolean {
    return this.companySubscription?.status === 'active';
  }

  /**
   * The header trigger relies on PrimeNG's own anchoring; the drawer/sidebar
   * row has to be pushed clear of the sidebar manually.
   */
  get panelPosition(): Record<string, string> {
    if (this.variant === 'avatar') {
      return {};
    }
    return { position: 'absolute', left: this.isSidebarExpanded ? '270px' : '65px', bottom: '-100px' };
  }

  get userName(): string {
    return `${this.userService.getUserData()?.first_name} ${this.userService.getUserData()?.last_name}`;
  }

  showPopover(event?: Event): void {
    if (this.popover && this.userMenuButton) {
      this.isMouseOverButton = true;
      const targetEvent = event || new MouseEvent('mouseenter', { bubbles: true, cancelable: true });
      this.popover.show(targetEvent, this.userMenuButton.nativeElement);
    }
  }

  hidePopover(event?: Event): void {
    this.isMouseOverButton = false;
    // Solo cerrar si el mouse no está sobre el popover
    if (!this.isMouseOverPopover) {
      setTimeout(() => {
        if (!this.isMouseOverButton && !this.isMouseOverPopover && this.popover) {
          this.popover.hide();
        }
      }, 100);
    }
  }

  onPopoverMouseEnter(): void {
    this.isMouseOverPopover = true;
  }

  onPopoverMouseLeave(): void {
    this.isMouseOverPopover = false;
    // The header menu opens on click, so it must not close on mouse-out.
    if (this.variant === 'avatar') {
      return;
    }
    // Solo cerrar si el mouse tampoco está sobre el botón
    if (!this.isMouseOverButton) {
      setTimeout(() => {
        if (!this.isMouseOverButton && !this.isMouseOverPopover && this.popover) {
          this.popover.hide();
        }
      }, 100);
    }
  }
}
